// frontend/src/components/GlobalSearchModal/ReportTemplateConfig.js
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Divider, Space, Button, Upload, message, Card, Switch, Row, Col, ColorPicker } from 'antd';
import { UploadOutlined, EyeOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

const { Option } = Select;

const ReportTemplateConfig = ({ visible, onClose, onSave, initialConfig }) => {
  const [form] = Form.useForm();
  const [logo, setLogo] = useState(initialConfig?.logo || null);
  const [customColors, setCustomColors] = useState(initialConfig?.customColors || {
    primary: '#1890ff',
    secondary: '#f0f0f0',
    text: '#000000'
  });

  useEffect(() => {
    if (visible && initialConfig) {
      form.setFieldsValue(initialConfig);
      setLogo(initialConfig.logo || null);
      setCustomColors(initialConfig.customColors || {
        primary: '#1890ff',
        secondary: '#f0f0f0',
        text: '#000000'
      });
    }
  }, [visible, initialConfig, form]);

  const handleSave = (values) => {
    const config = {
      ...values,
      logo: logo,
      customColors: values.colorScheme === 'custom' ? customColors : undefined
    };
    onSave(config);
    message.success('Template configuration saved successfully!');
    onClose();
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }
    
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setLogo(e.target.result);
      message.success('Logo uploaded successfully!');
    };
    reader.readAsDataURL(file);
    return false;
  };

  const removeLogo = () => {
    setLogo(null);
    message.info('Logo removed');
  };

  return (
    <Modal
      title="Report Template Configuration"
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={() => form.submit()}>
          Save Configuration
        </Button>
      ]}
      width={700}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        initialValues={initialConfig || {
          templateType: 'detailed',
          includeLogo: true,
          includeSummary: true,
          includeFooter: true,
          includeCharts: false,
          companyName: 'Our Company',
          reportTitle: 'Ticket Analysis Report',
          colorScheme: 'blue',
          pageOrientation: 'landscape'
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="templateType" label="Template Type" rules={[{ required: true }]}>
              <Select>
                <Option value="detailed">Detailed Report</Option>
                <Option value="summary">Executive Summary</Option>
                <Option value="analytical">Analytical Dashboard</Option>
                <Option value="minimal">Minimal Report</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="pageOrientation" label="Page Orientation" rules={[{ required: true }]}>
              <Select>
                <Option value="portrait">Portrait</Option>
                <Option value="landscape">Landscape</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="reportTitle" label="Report Title" rules={[{ required: true }]}>
          <Input placeholder="Enter report title" />
        </Form.Item>

        <Form.Item name="companyName" label="Company Name" rules={[{ required: true }]}>
          <Input placeholder="Enter company name" />
        </Form.Item>

        <Form.Item label="Company Logo" extra="Recommended size: 200x60px">
          <Space direction="vertical">
            <Upload
              beforeUpload={beforeUpload}
              showUploadList={false}
              accept="image/png, image/jpeg, image/jpg, image/svg+xml"
            >
              <Button icon={<UploadOutlined />}>Upload Logo</Button>
            </Upload>
            {logo && (
              <div>
                <img src={logo} alt="Logo preview" style={{ maxWidth: 200, maxHeight: 60, border: '1px solid #d9d9d9', padding: 4 }} />
                <Button type="link" danger onClick={removeLogo} size="small">
                  Remove
                </Button>
              </div>
            )}
          </Space>
        </Form.Item>

        <Form.Item name="colorScheme" label="Color Scheme" rules={[{ required: true }]}>
          <Select onChange={(value) => {
            if (value !== 'custom') {
              setCustomColors({
                primary: '#1890ff',
                secondary: '#f0f0f0',
                text: '#000000'
              });
            }
          }}>
            <Option value="blue">Corporate Blue</Option>
            <Option value="green">Professional Green</Option>
            <Option value="purple">Executive Purple</Option>
            <Option value="orange">Modern Orange</Option>
            <Option value="custom">Custom Colors</Option>
          </Select>
        </Form.Item>

        {form.getFieldValue('colorScheme') === 'custom' && (
          <Card size="small" title="Custom Colors" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Primary Color">
                  <ColorPicker
                    value={customColors.primary}
                    onChange={(color) => setCustomColors(prev => ({ ...prev, primary: color.toHexString() }))}
                    showText
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Secondary Color">
                  <ColorPicker
                    value={customColors.secondary}
                    onChange={(color) => setCustomColors(prev => ({ ...prev, secondary: color.toHexString() }))}
                    showText
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="Text Color">
                  <ColorPicker
                    value={customColors.text}
                    onChange={(color) => setCustomColors(prev => ({ ...prev, text: color.toHexString() }))}
                    showText
                  />
                </Form.Item>
              </Col>
            </Row>
          </Card>
        )}

        <Divider>Report Sections</Divider>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="includeLogo" valuePropName="checked" label="Logo">
              <Switch checkedChildren="Include" unCheckedChildren="Exclude" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="includeSummary" valuePropName="checked" label="Executive Summary">
              <Switch checkedChildren="Include" unCheckedChildren="Exclude" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="includeFooter" valuePropName="checked" label="Footer">
              <Switch checkedChildren="Include" unCheckedChildren="Exclude" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="includeCharts" valuePropName="checked" label="Charts">
              <Switch checkedChildren="Include" unCheckedChildren="Exclude" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

ReportTemplateConfig.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  initialConfig: PropTypes.object
};

ReportTemplateConfig.defaultProps = {
  initialConfig: null
};

export default ReportTemplateConfig;